import { CONTACT_SUPPORT_URL, ZENDESK_URL } from '@common/constants';
import {
  CANCEL_SUBSCRIPTION_REQUEST_SOURCE,
  CancelSubscriptionRequestSource,
  PROPHECY_AI_SUPPORT_URL
} from '@common/credits/ProfessionalEdition/constants';
import { useIsBillingAdmin, useProfessionalCreditStatus } from '@common/credits/ProfessionalEdition/hooks';
import { TEAM_STATE, PLAN_TYPES } from '@common/credits/ProfessionalEdition/types';
import { useProfessionalEditionCreditsContext } from '@common/credits/ProfessionalEdition/useProfessionalEditionCreditsContext';
import { isFreePlan } from '@common/credits/ProfessionalEdition/utils';
import { EntityEvents } from '@common/user-analytics';
import { useUserWorkspace } from '@common/VerticalApps/hooks';
import { useIsFinanceVerticalApp } from '@common/VerticalApps/utils';
import { theme } from '@prophecy/theme';
import { Button, Dialog, Stack } from '@prophecy/ui-v3';
import { DialogSize } from '@prophecy/ui-v3/Dialog/types';
import { Text } from '@prophecy/ui-v3/Typography';
import { toast } from '@prophecy/ui-v3/ui-compat';
import { usePersistentCallback } from '@prophecy/utils/react/hooks';
import { useMixPanel } from 'frontend/core/src/context/mixpanel/context';
import { useUser } from 'frontend/core/src/context/user';
import { useState } from 'react';
import styled from 'styled-components';

import { PricingTierCard } from '../../../components/PricingTierCard';
import { DEFAULT_PLANS_CONFIG, PRO_PLAN_IMPORTS_FEATURE } from '../constants';
import { cancelSubscription, upgradeToPro } from '../data/api';
import { PlanConfig } from '../types';
import { computePlanSubtitle, formatPlanPrice, formatPlanUnit } from '../utils';

import type { Plan } from '@common/credits/ProfessionalEdition/types';

export const PricingTiers = ({ plans }: { plans: Plan[] }) => {
  const {
    meteringInfo: { plan: activePlan, teamState, teamID }
  } = useProfessionalEditionCreditsContext();
  const { user } = useUser();

  const [planUpdateInProgress, setPlanUpdateInProgress] = useState<PLAN_TYPES | undefined>(undefined);

  // disable the free plan if plan is null and team state is disabled - this is to ensure that the free plan is not shown for any new teams
  const shouldDisableFreePlan = activePlan === undefined && teamState === TEAM_STATE.disabled;

  const isBillingAdmin = useIsBillingAdmin();
  const isFinanceVerticalApp = useIsFinanceVerticalApp();
  const { isOutOfCredits, isPlanExpired } = useProfessionalCreditStatus();

  const { track } = useMixPanel();

  const { currentWorkspace } = useUserWorkspace();
  const workspaceAlias = currentWorkspace?.workspaceAlias || currentWorkspace?.name || '';

  const handlePlanUpdate = usePersistentCallback(async (planType: PLAN_TYPES) => {
    // For PRO only upgrading is allowed
    if (planType === PLAN_TYPES.Pro) {
      try {
        setPlanUpdateInProgress(planType);
        const { data, success } = await upgradeToPro({
          email: user.email,
          name: `${user.firstName} ${user.lastName}`,
          id: user.id,
          teamId: teamID
        });
        if (!success) {
          throw new Error('Failed to upgrade to Prophecy Pro');
        }
        setPlanUpdateInProgress(undefined);

        track(EntityEvents.settings.billing.upgradeToPro, {
          fromPlan: activePlan,
          toPlan: planType,
          teamId: teamID
        });

        window.open(data.checkoutUrl, '_blank');
      } catch (error) {
        console.error(error);
        toast({
          title: 'Failed to upgrade to Prophecy Pro',
          variant: 'error',
          content: 'Please try again later',
          closeable: true
        });
      }
    } else if (isFreePlan(planType)) {
      // For downgrading to the free plan, redirect to zendesk. Upgrading to it is not possible since it's a free plan.

      const downgradeEvent =
        planType === PLAN_TYPES.FreeTrial
          ? EntityEvents.settings.billing.downgradeToFreeTrial
          : EntityEvents.settings.billing.downgradeToStarter;
      track(downgradeEvent, {
        fromPlan: activePlan,
        toPlan: planType,
        teamId: teamID
      });

      window.open(ZENDESK_URL, '_blank');
    } else if (planType === PLAN_TYPES.Enterprise || planType === PLAN_TYPES.Express) {
      // For upgrading to enterprise/express plans, redirect to contact support url
      const url = new URL(CONTACT_SUPPORT_URL);
      url.searchParams.set('plan', planType);
      url.searchParams.set('ref', 'app');
      const upgradeEvent =
        planType === PLAN_TYPES.Enterprise
          ? EntityEvents.settings.billing.upgradeToEnterprise
          : EntityEvents.settings.billing.upgradeToExpress;
      track(upgradeEvent, {
        fromPlan: activePlan,
        toPlan: planType,
        teamId: teamID
      });

      window.open(url.toString(), '_blank');
    }
  });

  const handleSubscriptionCancel = usePersistentCallback(
    async (requestSource: CancelSubscriptionRequestSource, plan: PLAN_TYPES) => {
      try {
        setPlanUpdateInProgress(plan);
        const { success } = await cancelSubscription(teamID, user.email, workspaceAlias, requestSource);
        setPlanUpdateInProgress(undefined);
        if (!success) {
          throw new Error('Failed to cancel subscription');
        }
        Dialog.success({
          'data-test-id': 'cancel-subscription-success-dialog',
          title:
            requestSource === CANCEL_SUBSCRIPTION_REQUEST_SOURCE.SwitchToStarter
              ? 'Starter Plan Request Received'
              : 'Subscription Cancellation Request Received',
          size: DialogSize.l,
          children: (
            <Stack direction='vertical' gap={theme.spaces.x12} align='center' padding={`${theme.spaces.x12} 0`}>
              <Text level='md' tone={theme.colors.gray600}>
                {requestSource === CANCEL_SUBSCRIPTION_REQUEST_SOURCE.SwitchToStarter
                  ? "We're sorry to see you go. We've received your request to switch to the Starter plan."
                  : "We're sorry to see you go. We've received your request to cancel your subscription."}
              </Text>
              <Text level='md' tone={theme.colors.gray600}>
                Our team will get in touch with you shortly with the next steps.
              </Text>
            </Stack>
          )
        });
      } catch (error) {
        console.error(error);
        setPlanUpdateInProgress(undefined);
        Dialog.alert({
          'data-test-id': 'cancel-subscription-failed-dialog',
          title:
            requestSource === CANCEL_SUBSCRIPTION_REQUEST_SOURCE.SwitchToStarter
              ? 'Failed to switch to Starter plan'
              : 'Failed to cancel subscription',
          size: DialogSize.l,
          children: (
            <Stack direction='vertical' gap={theme.spaces.x12} align='center' padding={`${theme.spaces.x12} 0`}>
              <Text level='md' tone={theme.colors.gray600}>
                {requestSource === CANCEL_SUBSCRIPTION_REQUEST_SOURCE.SwitchToStarter
                  ? "We weren't able to process your request to switch to the Starter plan at this time."
                  : "We weren't able to process your cancellation request at this time."}
              </Text>
              <Text level='md' tone={theme.colors.gray600}>
                Please try again later or contact our{' '}
                <Button
                  size='l'
                  elementType='anchor'
                  variant='link'
                  href={PROPHECY_AI_SUPPORT_URL}
                  target='_blank'
                  rel='noopener noreferrer'
                  data-test-id='cancel-subscription-failed-contact-support-link'>
                  support team
                </Button>{' '}
                if the issue persists.
              </Text>
            </Stack>
          ),
          closeButton: null
        });
      }
    }
  );
  const computePlanActionConfig = usePersistentCallback((plan: Plan): PlanConfig['action'] => {
    const defaultPlanConfig = DEFAULT_PLANS_CONFIG[plan.name as PLAN_TYPES];
    const isCurrentPlanActive = plan.name === activePlan;
    const isDowngradeToFreePlan = isFreePlan(plan.name) && activePlan === PLAN_TYPES.Pro;
    const isFreePlanDisabled = shouldDisableFreePlan && isFreePlan(plan.name);

    if (isFreePlanDisabled) {
      return {
        label: 'Not available',
        variant: 'secondaryGray',
        onClick: undefined,
        disabledMessage: `${plan.displayName} plan is not available for new workspaces. Please upgrade to Pro plan.`
      };
    }

    if (isCurrentPlanActive) {
      // A free active plan (Starter/FreeTrial) has no subscription to cancel or renew.
      if (isFreePlan(plan.name)) {
        return {
          label: 'Current Plan',
          variant: 'secondaryGray',
          onClick: undefined,
          // Expired trial takes precedence over exhausted credits; both take precedence over
          // "plan is active", which would contradict the credit limit banner shown alongside
          disabledMessage: isPlanExpired
            ? `Your ${plan.displayName} plan has expired. Upgrade to Pro to continue.`
            : isOutOfCredits
              ? `You've used all available ${plan.displayName} credits. Upgrade to Pro to continue.`
              : `${plan.displayName} plan is active for this workspace`
        };
      }
      // A paid plan expires when its renewal fails — offer renewal (same checkout flow as
      // upgrading) directly from its card
      if (isPlanExpired) {
        return {
          label: 'Renew now',
          variant: 'primary',
          onClick: () => handlePlanUpdate(plan.name as PLAN_TYPES),
          isLoading: planUpdateInProgress === plan.name,
          disabledMessage: isBillingAdmin
            ? ''
            : `Please contact your workspace admin to renew the ${plan.displayName} plan`
        };
      }
      return {
        label: CANCEL_SUBSCRIPTION_REQUEST_SOURCE.CancelSubscription,
        variant: 'secondary',
        isLoading: planUpdateInProgress === plan.name,
        onClick: () =>
          handleSubscriptionCancel(CANCEL_SUBSCRIPTION_REQUEST_SOURCE.CancelSubscription, plan.name as PLAN_TYPES)
      };
    }

    if (isDowngradeToFreePlan) {
      if (plan.name === PLAN_TYPES.FreeTrial) {
        return {
          label: 'Not available',
          variant: 'secondaryGray',
          onClick: undefined,
          disabledMessage: 'Free trial is not available after upgrading to Pro plan'
        };
      }
      return {
        label: 'Switch to Starter',
        variant: 'secondary',
        isLoading: planUpdateInProgress === plan.name,
        onClick: () =>
          handleSubscriptionCancel(CANCEL_SUBSCRIPTION_REQUEST_SOURCE.SwitchToStarter, plan.name as PLAN_TYPES),
        disabledMessage: isBillingAdmin ? '' : `Please contact workspace admin to downgrade to ${plan.displayName} plan`
      };
    }

    return {
      label: defaultPlanConfig.action.label,
      variant: defaultPlanConfig.action.variant,
      onClick: () => handlePlanUpdate(plan.name as PLAN_TYPES),
      isLoading: planUpdateInProgress === plan.name
    };
  });

  const formatCreditsFeature = (plan: Plan): string | null => {
    if (plan.includedCredits == null) return null;
    const base = `${plan.includedCredits} included credits ${formatPlanUnit(plan.price, plan.creditsUnit)}`;
    return plan.name === PLAN_TYPES.Express ? `${base} (shared annual pool)` : base;
  };

  const computePlansConfig = (): Array<PlanConfig> => {
    return plans.map((plan) => {
      const planStaticConfig = DEFAULT_PLANS_CONFIG[plan.name as PLAN_TYPES];
      const creditsFeature = formatCreditsFeature(plan);
      const usersLimit = plan.maxUsers && plan.maxUsers > 1 ? `up to ${plan.maxUsers} users` : null;
      const staticFeatures = isFinanceVerticalApp
        ? planStaticConfig.features.filter((feature) => feature !== PRO_PLAN_IMPORTS_FEATURE)
        : planStaticConfig.features;
      return {
        name: plan.name,
        displayName: plan.displayName,
        price: formatPlanPrice(plan.price),
        priceSubtitle: computePlanSubtitle(plan),
        unit: formatPlanUnit(plan.price, plan.unit),
        description: planStaticConfig.description,
        features: [...(creditsFeature ? [creditsFeature] : []), ...(usersLimit ? [usersLimit] : []), ...staticFeatures],
        footer: planStaticConfig.footer,
        // Resolve the static plan name to the edition-specific plan (Pro extends Starter in Professional but FreeTrial in Structured Finance)
        extendsFrom: plans.find(
          ({ name }) =>
            name === planStaticConfig.extendsFrom || (isFreePlan(planStaticConfig.extendsFrom) && isFreePlan(name))
        )?.displayName,
        action: computePlanActionConfig(plan)
      };
    });
  };

  const plansConfig = computePlansConfig();
  return (
    <Stack direction='vertical' gap={theme.spaces.x16}>
      <Text level='sm' weight={theme.fontWeight.semiBold} tone={theme.colors.gray900}>
        All plans
      </Text>
      <PricingTiersGrid>
        {plansConfig.map((plan) => {
          return (
            <PricingTierCard
              key={plan.name}
              title={plan.displayName}
              price={plan.price}
              priceSubtitle={plan.priceSubtitle}
              period={plan.unit}
              description={plan.description}
              features={plan.features}
              footer={plan.footer}
              action={plan.action}
              isActive={plan.name === activePlan}
              extendsFrom={plan.extendsFrom}
            />
          );
        })}
      </PricingTiersGrid>
    </Stack>
  );
};

// One column per plan; the 3 rows (header, action button, features) are shared with the
// cards via subgrid so buttons and features start at the same height across cards
const PricingTiersGrid = styled.div`
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: minmax(0, 1fr);
  grid-template-rows: auto auto 1fr;
  gap: ${theme.spaces.x32};
  width: 100%;
`;
